import { DateTime } from "luxon";

import Filters from "../../../../filters";

import { DisplayStatus } from "./consts";

import type { FormattedFileImportItem } from "./types";

type FormatWireImportBatchOptions = {
  institutionAccount: FormattedFileImportItem["institutionAccount"];
  dualApprovalId: FormattedFileImportItem["dualApprovalId"];
  userId: FormattedFileImportItem["userId"];
};

export const formatWireImportBatchAsFileImportItem = (
  batch: API.WireImport.WireImportBatch,
  options: FormatWireImportBatchOptions,
): FormattedFileImportItem => {
  const { institutionAccount, dualApprovalId, userId } = options;

  const amountCents = batch.total_amount as Cents;
  const netTotalDollars = Filters.currency(amountCents, {
    hasDecimal: false,
  });
  const netTotalDollarsWithSign = `-${netTotalDollars}`;

  return {
    uuid: batch.id,
    type: "wire",
    // matches the file import list, which falls back to the file name
    title: batch.description || batch.file_name,
    state: batch.state,
    displayStatus: DisplayStatus[batch.state] || DisplayStatus.unknown,
    institutionAccount,
    organizationFile: batch.file_name
      ? { file_name: batch.file_name, file_type: "csv" }
      : null,
    netTotalDollars,
    netTotalDollarsWithSign,
    dateRange: batch.created_at
      ? DateTime.fromISO(batch.created_at).toFormat("MMM d, yyyy")
      : "",
    paymentCount: batch.wire_count,
    paymentCreditCount: 0,
    paymentDebitCount: 0,
    isExpedited: false,
    dualApprovalId,
    userId,
    fileName: batch.file_name,
    isReversible: false,
    isCancellable: null,
    hasReversal: false,
  };
};

/** Mirrors `NachaPassthroughSubmission.is_cancellable`. */
const PASSTHROUGH_CANCELABLE_STATUSES = new Set([
  DisplayStatus.pending_approval,
  DisplayStatus.scheduled,
]);

/** Whether a pass-through file in this display status can still be canceled. */
export const isPassthroughCancelable = (displayStatus: string): boolean =>
  PASSTHROUGH_CANCELABLE_STATUSES.has(displayStatus);

export const isPassthroughAnalyzing = (
  state: API.NachaPassthrough.SubmissionState,
): boolean => state === "uploaded" || state === "analyzing";

/**
 * Determine whether the cancel option should be shown for a file import item.
 *
 * - ACH: cancellable when the backend reports `is_cancellable`
 * - Wire: cancellable only when pending approval, the batch has a dual approval,
 *   and the current user is the requester (same logic as individual wire cancel
 *   in paymentActivity/helpers.ts).
 * - Pass-through: cancellable when pending approval or scheduled.
 */
export const showFileImportCancelOption = (
  item: FormattedFileImportItem,
  options?: { currentUserUuid?: API.UserId },
): boolean => {
  const { type, displayStatus, dualApprovalId, userId } = item;

  if (type === "ach") {
    return item.isCancellable === true;
  }

  if (type === "ach_passthrough") {
    return isPassthroughCancelable(displayStatus);
  }

  if (type === "wire") {
    const isRequester =
      Boolean(options?.currentUserUuid) &&
      Boolean(userId) &&
      options?.currentUserUuid === userId;
    return (
      displayStatus === DisplayStatus.pending_approval &&
      dualApprovalId !== null &&
      isRequester
    );
  }

  return false;
};

// Status filter values that are not file import states themselves
const FILTER_STATE_LABELS: Record<string, string> = {
  completed_with_issues: DisplayStatus.partially_completed,
};

/**
 * ACH, wire, reversal and pass-through files name equivalent states
 * differently, so expand each filter state to every state shown with the same
 * label. Values with no known label pass through unchanged.
 */
export const toFileImportStates = (states: string[]): string[] => [
  ...new Set(
    states.flatMap((state) => {
      const label = DisplayStatus[state] ?? FILTER_STATE_LABELS[state];
      const matching = Object.keys(DisplayStatus).filter(
        (key) => key !== "unknown" && DisplayStatus[key] === label,
      );
      return matching.length ? matching : [state];
    }),
  ),
];

/** Rewrites the `state_in` param of a file import query string. */
export const withFileImportStates = (queryString?: string) => {
  if (!queryString) {
    return queryString;
  }
  return queryString
    .split("&")
    .map((part) => {
      const [key, value] = part.split("=");
      if (key !== "state_in" || !value) {
        return part;
      }
      const states = value.split(",").map(decodeURIComponent);
      return `state_in=${toFileImportStates(states)
        .map(encodeURIComponent)
        .join(",")}`;
    })
    .join("&");
};
